import { createSlice } from '@reduxjs/toolkit';
import {
  createFSOrderWithTwoFileTypes,
  getOrderDetails,
  addImageFromGallery,
  addImageFromUploaded,
  deleteImageFS,
  getFSOrderList,
  deleteFSOrder,
  updateFSOrderDetails,
  generateFSOrderDetails,
  generateFSOrderPrice,
  updateFSOrderPrice,
  sendFSImageFeedback,
  publishOrderInShopify,
} from './operations';

import { TESTTASK_ID, TEST_IMAGE_ID } from 'utils/configs';
import { roundToDecimals } from 'services/roundToDecimals';

import defaultInputImage from 'media/images/test_input.jpg';
import defaultResultImage from 'media/images/test_result.jpg';

const defaultTask = {
  id: TESTTASK_ID,
  name: 'Example',
  images: [
    {
      id: TEST_IMAGE_ID,
      location: defaultInputImage,
      location_basic: defaultInputImage,
      location_large: defaultInputImage,
      location_small: defaultInputImage,
      media_type: 'image',
      source: {
        type: 'test',
      },
    },
    {
      id: `${TEST_IMAGE_ID}2`,
      location: defaultResultImage,
      location_basic: defaultResultImage,
      location_large: defaultResultImage,
      location_small: defaultResultImage,
      media_type: 'image',
      source: {
        type: 'test',
      },
    },
  ],
  details: {
    subject: 'Example',
    description:
      "I'm a product description. I'm a great place to add more details about your product such as sizing, material, etc.",
    info: "I'm a product info. I'm a great place to add more details about your product such as care and cleaning instructions.",
  },
};

let fashionStoreInitialState = {
  fsOrderList: [],
  fsTextDataForOrder: {
    name: '',
    subject: '',
    description: '',
    info: '',
    feedback: 'not-rated',
  },
  fsOrderImages: [],
  integrations: {
    shopify: { product_id: null },
  },
  price: {
    main: { currency: null, price: null },
    generated: {
      country: null,
      currency: null,
      recommended_price: null,
      min_range: null,
      max_range: null,
      average_price: null,
      price_sources: [],
    },
  },
  fsIsLoading: false,
  fsResponseErrorMessage: null,
};

const handlePending = state => {
  state.fsResponseErrorMessage = null;
  state.fsIsLoading = true;
};

const handleRejected = (state, { payload }) => {
  state.fsResponseErrorMessage = payload;
  state.fsIsLoading = false;
};

export const fashionStoreSlice = createSlice({
  name: 'fashionstore',
  initialState: fashionStoreInitialState,

  reducers: {
    fsSetShopifyParams(state, { payload }) {
      state.integrations.shopify = {
        ...state.integrations.shopify,
        ...payload,
      };
    },
    fsResetTaskList(state) {
      state.fsOrderList = [];
    },

    fsResetFsState(state) {
      state.fsOrderImages = [];
      state.fsTextDataForOrder = fashionStoreInitialState.fsTextDataForOrder;
      state.price = fashionStoreInitialState.price;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(createFSOrderWithTwoFileTypes.pending, handlePending)
      .addCase(createFSOrderWithTwoFileTypes.rejected, handleRejected)
      .addCase(createFSOrderWithTwoFileTypes.fulfilled, state => {
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(getOrderDetails.pending, handlePending)
      .addCase(getOrderDetails.rejected, handleRejected)
      .addCase(getOrderDetails.fulfilled, (state, { payload }) => {
        state.fsOrderImages = payload.images;
        state.fsTextDataForOrder = {
          ...state.fsTextDataForOrder,
          ...payload.details,
          name: payload.name,
        };
        if (payload.price) {
          let mainPrice = state.price.main;
          let recommendedPrice = state.price.generated.recommended_price;
          let maxPrice = state.price.generated.max_range;
          let minPrice = state.price.generated.min_range;
          let averagePrice = state.price.generated.average_price;
          let roundedPriceSources = state.price.generated.price_sources;

          if (payload.price.main) {
            const { price, currency } = payload.price.main;
            mainPrice = {
              price: roundToDecimals(price, 2) || price || 0,
              currency,
            };
          }

          if (payload.price.generated) {
            const {
              recommended_price,
              max_range,
              min_range,
              average_price,
              price_sources,
            } = payload.price.generated;

            recommendedPrice =
              roundToDecimals(recommended_price, 2) ||
              recommended_price ||
              null;
            maxPrice = roundToDecimals(max_range, 2) || max_range || null;
            minPrice = roundToDecimals(min_range, 2) || min_range || null;
            averagePrice =
              roundToDecimals(average_price, 2) || average_price || null;
            roundedPriceSources =
              price_sources?.map(source => ({
                ...source,
                price: roundToDecimals(source.price, 2) || source.price || 0,
              })) || [];
          }

          const updatedData = {
            main: mainPrice,
            generated: {
              ...(payload.price.generated ? payload.price.generated : {}),
              recommended_price: recommendedPrice,
              max_range: maxPrice,
              min_range: minPrice,
              average_price: averagePrice,
              price_sources: [...roundedPriceSources].sort(
                (a, b) => b.price - a.price
              ),
            },
          };

          state.price = { ...state.price, ...updatedData };
        }

        if (payload.integrations?.shopify) {
          state.integrations.shopify = {
            ...state.integrations.shopify,
            ...payload.integrations.shopify,
          };
        }

        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(addImageFromGallery.pending, handlePending)
      .addCase(addImageFromGallery.rejected, handleRejected)
      .addCase(addImageFromGallery.fulfilled, (state, { payload }) => {
        state.fsOrderImages = payload.images;
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(addImageFromUploaded.pending, handlePending)
      .addCase(addImageFromUploaded.rejected, handleRejected)
      .addCase(addImageFromUploaded.fulfilled, (state, { payload }) => {
        state.fsOrderImages = payload.images;
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(deleteImageFS.pending, handlePending)
      .addCase(deleteImageFS.rejected, handleRejected)
      .addCase(deleteImageFS.fulfilled, (state, { payload }) => {
        const newArr = state.fsOrderImages.filter(item => item.id !== payload);
        state.fsOrderImages = newArr;
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(getFSOrderList.pending, handlePending)
      .addCase(getFSOrderList.rejected, handleRejected)
      .addCase(getFSOrderList.fulfilled, (state, { payload }) => {
        if (payload.length > 0) {
          state.fsOrderList = payload;
        } else {
          state.fsOrderList = [defaultTask];
        }
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(deleteFSOrder.pending, handlePending)
      .addCase(deleteFSOrder.rejected, handleRejected)
      .addCase(deleteFSOrder.fulfilled, (state, { payload }) => {
        state.fsOrderList = state.fsOrderList.filter(
          order => order.id !== payload
        );
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(updateFSOrderDetails.pending, handlePending)
      .addCase(updateFSOrderDetails.rejected, handleRejected)
      .addCase(updateFSOrderDetails.fulfilled, (state, { payload }) => {
        const keys = Object.keys(payload.newMeta);
        if (keys.length === 1 && keys[0] === 'name') {
          const updatedList = [...state.fsOrderList];
          updatedList.forEach(order => {
            if (order.id === payload.orderId) {
              order.name = payload.newMeta.name;
            }
          });
          state.fsOrderList = updatedList;
        } else {
          state.fsTextDataForOrder = {
            ...state.fsTextDataForOrder,
            ...payload.newMeta,
          };
        }
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(generateFSOrderDetails.pending, () => {})
      .addCase(generateFSOrderDetails.rejected, handleRejected)
      .addCase(generateFSOrderDetails.fulfilled, (state, { payload }) => {
        state.fsTextDataForOrder = {
          ...state.fsTextDataForOrder,
          ...payload,
          feedback: 'not-rated',
        };
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(generateFSOrderPrice.pending, () => {})
      .addCase(generateFSOrderPrice.rejected, handleRejected)
      .addCase(generateFSOrderPrice.fulfilled, (state, { payload }) => {
        const {
          recommended_price,
          max_range,
          min_range,
          average_price,
          price_sources,
        } = payload;

        const recommendedPrice =
          roundToDecimals(recommended_price, 2) || recommended_price;
        const maxPrice = roundToDecimals(max_range, 2) || max_range;
        const minPrice = roundToDecimals(min_range, 2) || min_range;
        const averagePrice = roundToDecimals(average_price, 2) || average_price;
        const roundedPriceSources = price_sources?.map(source => ({
          ...source,
          price: roundToDecimals(source.price, 2) || source.price,
        }));

        const updatedData = {
          ...payload,
          recommended_price: recommendedPrice,
          max_range: maxPrice,
          min_range: minPrice,
          average_price: averagePrice,
          price_sources: [...roundedPriceSources].sort(
            (a, b) => b.price - a.price
          ),
        };

        state.price.generated = { ...state.price.generated, ...updatedData };

        state.price.main = {
          ...state.price.main,
          price: recommendedPrice,
          currency: payload.currency || state.price.generated.currency,
        };
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(updateFSOrderPrice.pending, () => {})
      .addCase(updateFSOrderPrice.rejected, handleRejected)
      .addCase(updateFSOrderPrice.fulfilled, (state, { payload }) => {
        const keys = Object.keys(payload);
        keys.forEach(key => {
          state.price.main[key] = payload[key];
        });

        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      })
      .addCase(sendFSImageFeedback.pending, state => {
        state.fsResponseErrorMessage = null;
      })
      .addCase(sendFSImageFeedback.rejected, handleRejected)
      .addCase(sendFSImageFeedback.fulfilled, (state, { payload }) => {
        state.fsTextDataForOrder.feedback = payload.value;
        state.fsResponseErrorMessage = null;
      })
      .addCase(publishOrderInShopify.pending, handlePending)
      .addCase(publishOrderInShopify.rejected, handleRejected)
      .addCase(publishOrderInShopify.fulfilled, state => {
        state.fsIsLoading = false;
        state.fsResponseErrorMessage = null;
      });
  },
});

export const { fsSetShopifyParams, fsResetTaskList, fsResetFsState } =
  fashionStoreSlice.actions;
export const fashionStoreReducer = fashionStoreSlice.reducer;
